import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { PostalPreview } from "../../redux/slices/letterDispatch";

interface Props {
  open: boolean;
  preview: PostalPreview | null;
  loading?: boolean;
  error?: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const formatEur = (cents: number): string =>
  (cents / 100).toLocaleString("fi-FI", {
    style: "currency",
    currency: "EUR",
  });

export const PostalChargeConfirmModal: React.FC<Props> = ({
  open,
  preview,
  loading = false,
  error,
  confirmLabel = "Lähetä ja vahvista",
  onCancel,
  onConfirm,
}) => {
  const [showRecipients, setShowRecipients] = useState(false);

  const unitGross = preview?.unitPriceGrossCents ?? 0;
  const totalGross = preview?.estimatedGrossCents ?? 0;
  const totalPages = preview?.totalPages ?? 0;
  const postCount = preview?.postCount ?? 0;
  const emailCount = preview?.emailCount ?? 0;
  const vatRate = preview?.vatRate ?? 25.5;
  const exact = preview?.exact ?? true;

  const hasPostal = postCount > 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Vahvista postilähetys</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && preview && (
          <Stack spacing={2}>
            {hasPostal ? (
              <>
                <Typography variant="body1">
                  Tämä lähetys aiheuttaa{" "}
                  <strong>{formatEur(totalGross)}</strong>{" "}
                  {exact ? "" : "(arvioitu) "}
                  postikuluja (sis. ALV {vatRate
                    .toString()
                    .replace(".", ",")}{" "}
                  %).
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {postCount} postilähetystä, yhteensä {totalPages} sivua ×{" "}
                  {formatEur(unitGross)}
                  {emailCount > 0 && ` · ${emailCount} sähköpostitse`}
                </Typography>

                {!exact && (
                  <Alert severity="info">
                    Sivumäärä on arvio. Todellinen veloitus määräytyy tulostimen
                    raportoiman sivumäärän mukaan.
                  </Alert>
                )}

                <Box>
                  <Button
                    size="small"
                    onClick={() => setShowRecipients((s) => !s)}
                    endIcon={
                      showRecipients ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                  >
                    {showRecipients
                      ? "Piilota vastaanottajat"
                      : "Näytä vastaanottajat"}
                  </Button>
                  <Collapse in={showRecipients}>
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vastaanottaja</TableCell>
                          <TableCell align="right">Sivut</TableCell>
                          <TableCell align="right">Hinta</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.perRecipient.map((r) => (
                          <TableRow key={r.userId}>
                            <TableCell>{r.name}</TableCell>
                            <TableCell align="right">{r.pages}</TableCell>
                            <TableCell align="right">
                              {formatEur(r.grossCents)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Collapse>
                </Box>
              </>
            ) : (
              <Typography variant="body1">
                Tämä lähetys tehdään pelkästään sähköpostitse — postikuluja ei
                synny.
                {emailCount > 0 && ` (${emailCount} vastaanottajaa)`}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Peruuta
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading || !preview || !!error}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostalChargeConfirmModal;
